import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/base/card';
import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';
import { Badge } from '@/ui/base/badge';
import { toast } from 'sonner';
import {
  ShieldCheck,
  DatabaseZap,
  Scale,
  Save,
  History,
  AlertTriangle,
  ArrowLeft,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  usePoliticaPrevalencia,
  useUpdatePoliticaPrevalencia,
  useAuditoriaDecisoesSGTP,
  MODO_LABELS,
  MODO_DESCRICOES,
  type ModoConflitoSGTP,
} from './usePoliticaPrevalencia';
import { Spinner } from '@/ui/Spinner';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { PageHeader } from '@/ui/PageHeader';

const OPCOES: { valor: ModoConflitoSGTP; icon: typeof ShieldCheck; tom: string }[] = [
  { valor: 'SGTP_SEMPRE', icon: ShieldCheck, tom: 'border-primary/40' },
  { valor: 'MANUAL_TEMPORARIO', icon: DatabaseZap, tom: 'border-warning/30' },
  { valor: 'DECISAO_ASSISTIDA', icon: Scale, tom: 'border-success/30' },
];

export default function PoliticaPrevalenciaSGTPPage() {
  const navigate = useNavigate();
  const { data: politica, isLoading } = usePoliticaPrevalencia();
  const update = useUpdatePoliticaPrevalencia();
  const { data: auditoria, isLoading: auditLoading } = useAuditoriaDecisoesSGTP(20);

  const [modoSelecionado, setModoSelecionado] = useState<ModoConflitoSGTP>('DECISAO_ASSISTIDA');
  const [observacao, setObservacao] = useState('');

  useEffect(() => {
    if (politica) {
      setModoSelecionado(politica.modo);
      setObservacao(politica.observacao ?? '');
    }
  }, [politica]);

  const dirty =
    politica && (modoSelecionado !== politica.modo || observacao !== (politica.observacao ?? ''));

  const handleSalvar = async () => {
    if (!politica) return;
    try {
      await update.mutateAsync({
        id: politica.id,
        modo: modoSelecionado,
        observacao: observacao.trim() || null,
      });
      toast.success('Política atualizada e registrada na auditoria.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao atualizar política.');
    }
  };

  if (isLoading) return <Spinner fullPage />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Política de prevalência SGTP × Manual"
        description="Define institucionalmente como o sistema resolve divergências entre o dado oficial vindo do SGTP e edições manuais feitas no TEG."
      />

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Coluna principal — escolha do modo */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-base">Modo de conflito vigente</CardTitle>
              <CardDescription>
                Escolha como o sistema deve se comportar quando o dado do SGTP divergir de um
                registro manual. Esta política se aplica a todas as entidades sujeitas à integração
                (Titulares, Veículos e demais).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                {OPCOES.map(({ valor, icon: Icon, tom }) => {
                  const ativo = modoSelecionado === valor;
                  return (
                    <button
                      key={valor}
                      type="button"
                      onClick={() => setModoSelecionado(valor)}
                      className={`text-left rounded-xl border-2 p-4 transition-all ${
                        ativo
                          ? `${tom} bg-muted/40 shadow-xs`
                          : 'border-border hover:border-muted-foreground/40'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`mt-0.5 h-9 w-9 rounded-lg flex items-center justify-center ${ativo ? 'bg-primary/10' : 'bg-muted'}`}
                        >
                          <Icon
                            className={`h-5 w-5 ${ativo ? 'text-primary' : 'text-muted-foreground'}`}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{MODO_LABELS[valor]}</span>
                            {ativo && (
                              <Badge variant="default" className="text-[10px]">
                                Selecionado
                              </Badge>
                            )}
                            {politica?.modo === valor && !ativo && (
                              <Badge variant="outline" className="text-[10px]">
                                Vigente
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {MODO_DESCRICOES[valor]}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-2">
                <Label htmlFor="obs">Observação institucional (opcional)</Label>
                <Textarea
                  id="obs"
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  placeholder="Justificativa, referência normativa ou contexto da decisão."
                  rows={3}
                  maxLength={500}
                />
                <p className="text-[11px] text-muted-foreground">
                  {observacao.length}/500 — fica registrado na auditoria.
                </p>
              </div>

              {dirty && (
                <div className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 p-3">
                  <AlertTriangle className="h-4 w-4 text-foreground mt-0.5 shrink-0" />
                  <p className="text-xs text-foreground ">
                    Mudanças nesta política impactam imediatamente todos os fluxos de integração
                    SGTP. A alteração e a observação serão registradas de forma imutável na trilha
                    de auditoria.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar
                </Button>
                <ConfirmDialog
                  trigger={
                    <Button disabled={!dirty || update.isPending}>
                      <Save className="h-4 w-4 mr-1.5" /> Salvar política
                    </Button>
                  }
                  title="Confirmar nova política?"
                  description={`A política passará a ser "${MODO_LABELS[modoSelecionado]}". A mudança é registrada na auditoria.`}
                  confirmLabel={update.isPending ? 'Salvando...' : 'Confirmar'}
                  onConfirm={handleSalvar}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coluna lateral — auditoria */}
        <div className="space-y-4">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4 text-primary" /> Trilha de auditoria
              </CardTitle>
              <CardDescription>Últimas decisões registradas (imutáveis).</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLoading ? (
                <Spinner />
              ) : !auditoria || auditoria.length === 0 ? (
                <p className="text-xs text-muted-foreground">Sem registros ainda.</p>
              ) : (
                <ul className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                  {auditoria.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-lg border bg-muted/30 p-2.5 text-xs space-y-1"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <Badge variant="outline" className="text-[10px]">
                          {a.tipo_evento}
                        </Badge>
                        <span className="text-muted-foreground text-[10px]">
                          {format(new Date(a.created_at), 'dd/MM HH:mm', { locale: ptBR })}
                        </span>
                      </div>
                      {a.modo_anterior && a.modo_novo && (
                        <p className="text-muted-foreground">
                          <span className="line-through">{MODO_LABELS[a.modo_anterior]}</span>
                          {' → '}
                          <span className="font-medium text-foreground">
                            {MODO_LABELS[a.modo_novo]}
                          </span>
                        </p>
                      )}
                      {a.motivo && <p className="text-muted-foreground italic">"{a.motivo}"</p>}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
